import { useState } from 'react';
import type { ParsedGenome } from '../types';
import type { GenomeStats } from '../lib/stats';
import { ChromosomeChart } from './ChromosomeChart';
import { copyToClipboard, summaryNote } from '../lib/note';
import type { Theme } from '../lib/theme';
import { useVault } from '../lib/vault';
import { Explain } from './Explain';

interface Props {
  genome: ParsedGenome;
  stats: GenomeStats;
  theme: Theme;
}

function pct(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}

const FORMAT_LABEL: Record<string, string> = {
  '23andme': '23andMe raw',
  ancestry: 'AncestryDNA raw',
  vcf: 'VCF',
  unknown: 'Unknown',
};

export function Overview({ genome, stats, theme }: Props) {
  const [copied, setCopied] = useState(false);
  const { queueNote } = useVault();

  async function copySummary() {
    const date = new Date().toISOString().slice(0, 10);
    const body = summaryNote(genome, stats, { date });
    queueNote({
      kind: 'summary',
      title: `Genome summary — ${genome.meta.fileName}`,
      filename: `Genome summary ${date}`,
      body,
    });
    const ok = await copyToClipboard(body);
    setCopied(ok);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>
            Snapshot <span className="muted">· {FORMAT_LABEL[genome.format] ?? genome.format} · {genome.meta.fileName}</span>
            <Explain id="snapshot" />
          </h2>
          <button
            className="note-btn"
            style={{ marginLeft: 'auto' }}
            title="Copy a genome summary as an Obsidian note"
            onClick={copySummary}
          >
            {copied ? '✓ copied' : '＋ summary note'}
          </button>
        </div>
        <div style={{ height: 14 }} />
        <div className="stat-grid">
          <div className="stat">
            <div className="label">Variants<Explain id="metricVariants" /></div>
            <div className="value accent">{stats.total.toLocaleString()}</div>
            <div className="sub">across {stats.perChrom.length} chromosomes</div>
          </div>
          <div className="stat">
            <div className="label">Call rate<Explain id="metricCallRate" /></div>
            <div className="value">{pct(stats.callRate)}</div>
            <div className="sub">{stats.noCalls.toLocaleString()} no-calls</div>
          </div>
          <div className="stat">
            <div className="label">Heterozygosity<Explain id="metricHet" /></div>
            <div className="value">{pct(stats.hetRate)}</div>
            <div className="sub">{stats.heterozygous.toLocaleString()} het / {stats.homozygous.toLocaleString()} hom</div>
          </div>
          <div className="stat">
            <div className="label">Ti/Tv ratio<Explain id="metricTiTv" /></div>
            <div className="value">{stats.tiTv === null ? '—' : stats.tiTv.toFixed(2)}</div>
            <div className="sub">
              {stats.transitions.toLocaleString()} ti · {stats.transversions.toLocaleString()} tv
            </div>
          </div>
        </div>
        {genome.meta.truncated && (
          <div className="note" style={{ marginTop: 14 }}>
            This file is very large, so parsing stopped at the safety cap. The
            metrics above reflect the first {stats.total.toLocaleString()} variants read.
          </div>
        )}
      </div>

      <div className="card">
        <h2>Variants per chromosome<Explain id="chromosomes" /></h2>
        <ChromosomeChart data={stats.perChrom} theme={theme} />
      </div>
    </>
  );
}
